import { Box, Button, Paper, Theme } from "@material-ui/core";
import React, { FC, useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import { useIntl } from "react-intl";
import { checkTransactionStatus } from "../../api/CartApi";
import { LoadingSpinner } from "../common/LoadingSpinner";
import TranslatedMessage from "../translations/TranslatedMessage";
import Alert from "@material-ui/lab/Alert";
import { TransactionStatus } from "../../model/Cart/TransactionStatus";
import { routesPaths } from "../../Routes";
import { navigateTo } from "../../utils/Navigation";
import { useHistory } from "react-router-dom";
import UrlHelper from "../../utils/UrlHelpler";
import { isTranslationKey, TranslationKey } from "../../translations/TranslationKey";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: `${theme.spacing(2)}px`,
  },
  confirmMessage: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(4),
  },
  buttonBar: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
  },
  paidContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: `${theme.spacing(4)}px`,
    padding: theme.spacing(4),
  },
  countdown: {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
}));

interface LifePaymentConfirmationProps {
  datatransTrxId: string;
  languageKey: string;
  examSessionId: string;
  paidTransactionId?: string;
  onPaidTransaction: (transactionId: string) => void;
}

const LifePaymentConfirmation: FC<LifePaymentConfirmationProps> = ({
  datatransTrxId,
  languageKey,
  examSessionId,
  paidTransactionId,
  onPaidTransaction,
}) => {
  const classes = useStyles();
  const [checkingTransaction, setCheckingTransaction] = React.useState(false);
  const [transactionStatus, setTransactionStatus] = React.useState<TransactionStatus | null>(
    datatransTrxId === paidTransactionId ? TransactionStatus.Paid : null
  );
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [countdown, setCountdown] = React.useState(5);
  const [redirectTimer, setRedirectTimer] = React.useState<NodeJS.Timeout | null>(null);
  const intl = useIntl();
  const history = useHistory();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getTransactionStatus = () => {
    checkTransactionStatus(datatransTrxId)
      .then((transaction) => {
        setTimer(null);

        setTransactionStatus(transaction.status);

        if (transaction.status === TransactionStatus.Paid) {
          setCheckingTransaction(false);
          onPaidTransaction && onPaidTransaction(datatransTrxId);
        } else if (transaction.status !== TransactionStatus.Initialized) {
          setErrorMessage(transaction.message);
          setCheckingTransaction(false);
        } else {
          setTimer(
            setTimeout(() => {
              getTransactionStatus();
            }, 3000)
          );
        }
      })
      .catch((error) => {
        setTransactionStatus(TransactionStatus.Failed);
        setCheckingTransaction(false);
      });
  };

  useEffect(() => {
    if (transactionStatus === TransactionStatus.Paid && !redirectTimer) {
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            window.location.href = UrlHelper.getMyVbvUrl();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      setRedirectTimer(countdownInterval);
    }

    return () => {
      if (redirectTimer) {
        clearInterval(redirectTimer);
      }
    };
  }, [transactionStatus, redirectTimer]);

  useEffect(() => {
    if (!checkingTransaction && transactionStatus === null && datatransTrxId !== paidTransactionId) {
      setCheckingTransaction(true);
      setTimer(
        setTimeout(() => {
          getTransactionStatus();
        }, 100)
      );
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box className={classes.buttonBar}>
        {transactionStatus !== TransactionStatus.Paid && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => navigateTo(routesPaths.life(languageKey, examSessionId), history)}
          >
            {intl.formatMessage({ id: "lifeExams.back" })}
          </Button>
        )}
      </Box>
      {!checkingTransaction && (
        <Box className={classes.container}>
          {(transactionStatus === TransactionStatus.Failed || transactionStatus === TransactionStatus.Error) && (
            <Alert severity="error">
              <TranslatedMessage
                id="lifeExams.transactionFailed"
                values={{ transactionId: datatransTrxId }}
              />
              {errorMessage && isTranslationKey(errorMessage)
                ? (<TranslatedMessage id={errorMessage as TranslationKey} />)
                : (errorMessage)
              }
            </Alert>
          )}
          {transactionStatus === TransactionStatus.Cancelled && (
            <Alert severity="warning">
              <TranslatedMessage
                id="lifeExams.transactionCancelled"
                values={{ transactionId: datatransTrxId, errorMessage }}
              />
            </Alert>
          )}
          {transactionStatus === TransactionStatus.Paid && (
            <Box className={classes.paidContainer}>
              <h3><TranslatedMessage id="lifeExams.transactionPaid" /></h3>
              
              <div className={classes.countdown}>
                <TranslatedMessage id="lifeExams.redirectToMyVbv" values={{ secondsLeft: countdown }} />
              </div>
            </Box>
          )}
        </Box>
      )}

      {checkingTransaction && (
        <LoadingSpinner>
          <Paper className={classes.confirmMessage}>
            <h3>
              <TranslatedMessage id="startCertification.paymentAndCompletionStep.paymentConfirmationTitle" />
            </h3>
            <p>
              <TranslatedMessage id="startCertification.paymentAndCompletionStep.paymentConfirmationMessagePart1" />
              <br />
              <TranslatedMessage
                id="startCertification.paymentAndCompletionStep.paymentConfirmationMessagePart2"
                values={{ datatransTrxId }}
              />
            </p>
          </Paper>
        </LoadingSpinner>
      )}
    </>
  );
};

export default LifePaymentConfirmation;